import { isSameDay } from 'date-fns';
import { OpenPositionGridItem, OpenPositionItem } from '../../state';
import { InstrumentCategoryEnum, InstrumentStoreItem } from '../../types';
import { getDateFromDateTime, isValid, mergeNumericValues } from '../../utils';
import { getInitialPNl } from '../portfolio';

export const getPnL = <T extends OpenPositionItem | OpenPositionGridItem>(
  changedItem: InstrumentStoreItem,
  openPosition: T,
  rate: number,
): T | null => {
  if (!changedItem.price || !openPosition.instrument || !changedItem.ccy) {
    return null;
  }

  const { value: lastPrice } = changedItem.price;
  const { position, initPriceValue, value } = openPosition;
  const hasSubElement =
    (openPosition as OpenPositionGridItem).$subElements &&
    (openPosition as OpenPositionGridItem).$subElements!.length > 0;

  const {
    pNL,
    pNLPercentage,
    pNLDaily: pNLDailyCalculated,
    pNlDailyPercentage: pNlDailyPercentageCalculated,
  } = getInitialPNl({
    instrumentCategory: openPosition.instrument.instrumentCategory as InstrumentCategoryEnum,
    position,
    marketPriceSingle: lastPrice,
    initPriceValue,
    rate,
    grossProfitLoss: openPosition.pNL,
    closePrice: changedItem.closePrice,
    ric: openPosition.instrument.ric,
  });

  const inventoryDate = getDateFromDateTime(openPosition.inventoryDate);

  const subElements = hasSubElement
    ? ((openPosition as OpenPositionGridItem).$subElements || [])
        .map(el => getPnL(changedItem, el, rate) || el)
        .filter(_ => _ !== null)
    : [];

  const pNLDailyActual = !hasSubElement
    ? inventoryDate && isSameDay(inventoryDate, new Date())
      ? pNL
      : pNLDailyCalculated
    : mergeNumericValues(subElements, { include: ['pNLDaily'] }).pNLDaily;

  const pNLDailyPercentageActual = !hasSubElement
    ? inventoryDate && isSameDay(inventoryDate, new Date())
      ? pNLPercentage
      : pNlDailyPercentageCalculated
    : mergeNumericValues(subElements, { include: ['pNlDailyPercentage'] }).pNlDailyPercentage;

  const updatedValue = lastPrice * position;

  if (
    (isValid(pNL) && pNL !== openPosition.pNL) ||
    (isValid(pNLPercentage) && pNLPercentage !== openPosition.pNLPercentage) ||
    (isValid(pNLDailyActual) && pNLDailyActual !== openPosition.pNLDaily) ||
    (isValid(pNLDailyPercentageActual) && pNLDailyPercentageActual !== openPosition.pNlDailyPercentage) ||
    (isValid(updatedValue) && updatedValue !== value)
  ) {
    openPosition.value = updatedValue * rate;
    openPosition.pNLDaily = pNLDailyActual;
    openPosition.pNL = pNL;
    openPosition.pNLPercentage = pNLPercentage;
    openPosition.pNlDailyPercentage = pNLDailyPercentageActual;
    if (hasSubElement) {
      (openPosition as OpenPositionGridItem).$subElements = subElements.length ? subElements : undefined;
    }
    return openPosition;
  }

  return null;
};
